import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { debounce, pick, snakeCase, uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AppliedFiltersChips, {
  AppliedFiltersChipsCollectedData,
} from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { DriverSelectedFilter } from '~constants/enums';
import {
  OrderDispatchQuery,
  useDummyQuery,
} from '~pages/Dispatch/components/filters/orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';
import useDriverFilters, {
  driverFilterKeyBySelectedFilter,
} from '~pages/Settings/Administration/Drivers/hooks/useDriverFilters';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { useDriverLabelsQuery } from './driverFiltersQueries';

const dummyQueryFilters: DriverSelectedFilter[] = [
  DriverSelectedFilter.SENDER_COMPANY_TYPES,
];
const noneOptionFilters: DriverSelectedFilter[] = [];

const DriverFilters = observer(() => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<DriverSelectedFilter>();
  const { companyAssetsStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    appliedFilterChipsCollectedData,
    updateAvailableOptions,
    updateAppliedFilterChipsCollectedData,
    availableOptions,
  } = useDriverFilters();

  const [searchQuery, setSearchQuery] = useState<{ input: string; debounced: string }>({
    input: '',
    debounced: '',
  });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const companyTypesQuery = useDummyQuery(availableOptions.senderCompanyTypes);
  const labelsQuery = useDriverLabelsQuery(companyId);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const queries: Record<DriverSelectedFilter, OrderDispatchQuery> = {
      [DriverSelectedFilter.SENDER_COMPANY_TYPES]: companyTypesQuery,
      [DriverSelectedFilter.LABELS]: labelsQuery,
    };

    return queries[selectedFilter];
  }, [selectedFilter, companyTypesQuery, labelsQuery]);

  const isDummyQuery = useMemo(() => {
    return selectedFilter && dummyQueryFilters.includes(selectedFilter);
  }, [selectedFilter]);

  const searchValueChangeCallback = useMemo(() => {
    if (isDummyQuery) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [isDummyQuery]);

  const filterChipsData = useMemo(() => {
    const appliedFilters = pick(
      companyAssetsStore.driverFilters,
      Object.values(driverFilterKeyBySelectedFilter),
    );

    const collectedData = Object.entries(appliedFilterChipsCollectedData).reduce(
      (obj, [key, collectedValues]) => {
        const filter = driverFilterKeyBySelectedFilter[key as DriverSelectedFilter];

        obj[filter] = collectedValues;

        return obj;
      },
      {} as AppliedFiltersChipsCollectedData,
    );

    return { appliedFilters, collectedData };
  }, [JSON.stringify(companyAssetsStore.driverFilters), appliedFilterChipsCollectedData]);

  const hasFiltersApplied = useMemo(() => {
    const { appliedFilters } = filterChipsData;

    return Object.values(appliedFilters).filter((value) => Boolean(value)).length > 0;
  }, [JSON.stringify(filterChipsData.appliedFilters)]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = driverFilterKeyBySelectedFilter[selectedFilter];
    const options = companyAssetsStore.driverFilters[key];

    return options ?? [];
  }, [companyAssetsStore.driverFilters, selectedFilter]);

  const handleSelectedOptionsChange = useCallback(
    (selectedOptions: string[]) => {
      if (selectedFilter) {
        const key = driverFilterKeyBySelectedFilter[selectedFilter];
        const options = selectedOptions.length > 0 ? selectedOptions : undefined;

        companyAssetsStore.setDriverFilters({ [key]: options }, true);
      }
    },
    [selectedFilter],
  );

  const fetchMoreCallback = useMemo(() => {
    return activeQuery?.fetchMore;
  }, [activeQuery?.fetchMore]);

  const handleRemoveAllFilters = useCallback((filters: string | string[]) => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];
    const filtersObject = Object.fromEntries(
      filtersArray.map((filter) => [filter, undefined]),
    );

    companyAssetsStore.setDriverFilters(filtersObject, true);
  }, []);

  const updateAvailableOptionsAndFilterChips = useCallback(
    (key: DriverSelectedFilter, data: FilterMenuOption[]) => {
      updateAvailableOptions((state) => {
        return { ...state, [key]: uniqBy([...state[key], ...data], (i) => i.value) };
      });

      updateAppliedFilterChipsCollectedData(key, data);
    },
    [],
  );

  const getNoneOptionLabel = (selectedFilter: DriverSelectedFilter) => {
    if (noneOptionFilters.includes(selectedFilter)) {
      return `${t('dispatch.dispatch_v2.filters.no_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`;
    }
  };

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    if (!isDummyQuery && selectedFilter && activeQuery?.data) {
      updateAvailableOptionsAndFilterChips(selectedFilter, activeQuery?.data);
    }
  }, [
    updateAvailableOptionsAndFilterChips,
    selectedFilter,
    activeQuery?.data,
    isDummyQuery,
  ]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!selectedFilter) {
      labelsQuery.clear();
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
    }
  }, [isOpen]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={{
              [`&.${buttonClasses.root}`]: {
                backgroundColor: theme.semantic.surfaceRaised,
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                ...(hasFiltersApplied
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            }}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            searchValue={searchQuery.input}
            onSearchValueChange={searchValueChangeCallback}
            loadingReason={activeQuery?.loadingReason}
            selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`}
            options={activeQuery?.data ?? []}
            selectedOptions={selectedOptions}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            onFetchMore={fetchMoreCallback}
            selectNoneOptionLabel={getNoneOptionLabel(selectedFilter)}
          />
        )}
        {!selectedFilter &&
          Object.values(DriverSelectedFilter).map((filter) => (
            <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                  {t(`dispatch.dispatch_v2.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {(
                    companyAssetsStore.driverFilters[
                      driverFilterKeyBySelectedFilter[filter]
                    ] ?? []
                  ).length > 0 && (
                    <Check
                      sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>
      <AppliedFiltersChips
        {...filterChipsData}
        onRemoveFilter={handleRemoveAllFilters}
        onRemoveAllFilters={handleRemoveAllFilters}
      />
    </Box>
  );
});

export default DriverFilters;
